import { Button, Tab, Tabs } from "@nextui-org/react";
import React, { useEffect, useState } from "react";

import { getDataWithFilterUrl } from "../../controllers/APIController";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";

import { useParams } from "react-router-dom";
import { PageLoader } from "../../components/Loader";
import CardSkeletonContainer from "../../components/Skeleten/CardSkeletonContainer";
import PolicyCard from "../../components/Policies/PolicyCard";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { MyDocument } from "./Documenttest";

/**
 * Policies Component
 * Main page for managing ESG policies and procedures
 * Displays policies organized by domain categories with PDF report download
 * @returns {JSX.Element} Policies management interface
 */
const Policies = () => {
  // State for data management
  const [domains, setDomains] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reportDataFetched, setReportDataFetched] = useState(false);
  const [proceduresDetails, setProceduresDetails] = useState([]);

  // User and organization information from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  // URL parameters for routing
  const { _orgId: currentOrgId } = useParams();

  /**
   * Fetch domains, policies, and procedures data on component mount
   * Loads all necessary data for the policies page
   */
  useEffect(() => {
    // Fetch domains first
    getDataWithFilterUrl("domains")
      .then((data) => {
        setDomains(data.data);
        
        // Fetch policies for the organization
        getDataWithFilterUrl("policies", orgFilterUrl)
          .then((data) => {
            setPolicies(data.data);
            setIsLoading(false);
            
            // Fetch procedures for the organization
            getDataWithFilterUrl("procedures", orgFilterUrl).then((data) => {
              setProceduresDetails(data?.data || []);
              setReportDataFetched(true);
            });
          })
          .catch((error) => console.error("Error fetching policies:", error));
      })
      .catch((error) => console.error("Error fetching domains:", error));
  }, [orgFilterUrl]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">ESG Policies</h1>
            <p className="text-sm text-slate-600 mt-1">Manage sustainable practices and compliance</p>
          </div>
          
          {/* PDF Download Button */}
          {reportDataFetched && (
            <Button
              variant="ghost"
              className="text-slate-600 hover:text-slate-900 px-4 border border-slate-300"
            >
              <PDFDownloadLink
                document={
                  <MyDocument
                    policies={policies}
                    procedures={proceduresDetails}
                  />
                }
                fileName="sustainability-policy-report.pdf"
              >
                Download Policy Report
              </PDFDownloadLink>
            </Button>
          )}
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          {/* Policy Framework Overview */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Policy Framework</h2>
            <p className="text-slate-600 leading-relaxed">
              Our ESG software empowers organizations to implement and manage
              sustainable practices effectively. By ensuring compliance with
              regulations, our platform enhances your ability to make a positive
              impact on society and the environment. We provide tools to foster a
              culture of accountability and transparency, enabling continuous
              improvement in processes that reduce environmental footprints and
              promote social equity.
            </p>
          </div>

          {/* Policies Content Section */}
          {isLoading ? (
            <PageLoader
              loadingText="Loading Policies..."
              description="Fetching policy domains and organization data"
              variant="default"
            />
          ) : (
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
              {/* Section Header */}
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Policy Domains</h3>
                <p className="text-sm text-slate-600 mt-1">Browse policies by domain category</p>
              </div>
              
              {/* Domain Tabs and Policy Cards */}
              <div className="p-6">
                <Tabs 
                  variant="light"
                  classNames={{
                    tabList: "gap-6 w-full relative rounded-none p-0 border-b border-divider",
                    cursor: "w-full bg-emerald-600",
                    tab: "max-w-fit px-0 h-12",
                    tabContent: "group-data-[selected=true]:text-emerald-600"
                  }}
                >
                  {/* Render each domain as a tab */}
                  {domains &&
                    domains.map((domain) => (
                      <Tab key={domain.id} title={domain.attributes.Name}>
                        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {/* Filter and display policies for this domain */}
                          {policies &&
                            policies
                              .filter(
                                (policy) =>
                                  policy?.attributes?.DomainId?.data?.attributes
                                    ?.Name === domain.attributes.Name
                              )
                              .map((filteredPolicy) => (
                                <PolicyCard
                                  key={filteredPolicy.id}
                                  domain={domain}
                                  filteredPolicy={filteredPolicy}
                                  currOrg={currentOrgId}
                                />
                              ))}
                        </div>
                      </Tab>
                    ))}
                </Tabs>
              </div>
            </div>
          )}
        </div>
      </BreadCrumbsPage>
    </div>
  );
};

export default Policies;
